/**
 * Custom hook for fetching patients
 */

import { useState, useEffect } from 'react';
import { patientsApi } from '../api/patients';
import type { Patient, PatientStatus } from '../api/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface UsePatientsOptions {
  status_filter?: PatientStatus;
  cohort_id?: number;
  hospital_id?: number;
  search?: string;
  limit?: number;
  autoFetch?: boolean;
}

export function usePatients(options: UsePatientsOptions = {}) {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const { autoFetch = true, ...queryParams } = options;

  const fetchPatients = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await patientsApi.getPatients(queryParams);
      setPatients(response.patients);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch patients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchPatients();
    }
  }, [
    options.status_filter,
    options.cohort_id,
    options.hospital_id,
    options.search,
    autoFetch,
  ]);

  return {
    patients,
    loading,
    error,
    total,
    refetch: fetchPatients,
  };
}

/**
 * Hook for fetching single patient
 */
export function usePatient(patientId: number | null) {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPatient = async () => {
    if (!patientId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await patientsApi.getPatient(patientId);
      setPatient(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch patient');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatient();
  }, [patientId]);

  return {
    patient,
    loading,
    error,
    refetch: fetchPatient,
  };
}

/**
 * Hook for assigning patient to care team
 */
export function useAssignPatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignment: {
      patient_id: number;
      care_team_id: number;
    }) => patientsApi.assignPatient(assignment),
    onSuccess: () => {
      // Invalidate coordinator dashboard and patients queries
      queryClient.invalidateQueries({ queryKey: ['coordinator-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to assign patient';
      toast.error('Assignment Failed', {
        description: errorMessage,
      });
    },
  });
}

/**
 * Hook for unassigning patient from care team
 */
export function useUnassignPatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patientId: number) => patientsApi.unassignPatient(patientId),
    onSuccess: () => {
      // Invalidate coordinator dashboard and patients queries
      queryClient.invalidateQueries({ queryKey: ['coordinator-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      toast.success('Patient Unassigned', {
        description: 'Patient has been removed from care team successfully',
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to unassign patient';
      toast.error('Unassign Failed', {
        description: errorMessage,
      });
    },
  });
}